import React, { useState, useRef, useEffect } from 'react';
import { Search, User, Shield, Building2, Monitor, Receipt, CheckCheck, X, FileText, CreditCard, Banknote, LayoutList, History, Wallet, Lock, Unlock, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Settings } from 'lucide-react';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config.js';
import BilletajeGrid from './BilletajeGrid';
import CalculadoraVuelto from './CalculadoraVuelto';
import useCajaStore from '../store/useCajaStore';
import './Caja.css';

export default function Caja() {
    const { token, user } = useAuth();

    // --- POS State (Zustand) ---
    const {
        socio: storeSocio,
        deudas: storeDeudas,
        carrito,
        busqueda: storeBusqueda,
        searchResults,
        isSearching,
        setBusqueda,
        buscarSocios,
        seleccionarSocio,
        limpiarSocio,
        toggleSelection,
        removeFromCart,
        updateMontoItem,
        limpiarCarrito
    } = useCajaStore();

    // --- Local UI State ---
    const [sesionActiva, setSesionActiva] = useState(null);
    const [cargandoSesion, setCargandoSesion] = useState(true);
    const [listaCajas, setListaCajas] = useState([]);
    const [cajaIdSeleccionada, setCajaIdSeleccionada] = useState('');
    const [formasPago, setFormasPago] = useState([]);
    const [formaPagoId, setFormaPagoId] = useState('');
    const [montoRecibido, setMontoRecibido] = useState(0);
    const [activeTab, setActiveTab] = useState('ASOCIATIVO');
    const [procesando, setProcesando] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [cuentas, setCuentas] = useState([]);
    const [desembolsos, setDesembolsos] = useState([]);
    const [cargandoDesembolsos, setCargandoDesembolsos] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const ITEMS_PER_PAGE = 10;

    // Modals
    const [modals, setModals] = useState({
        cierreCaja: false,
        finalizarPago: false
    });
    const [operacion, setOperacion] = useState(null); // { tipo: 'DEPOSITO' | 'EXTRACCION', item: any }

    // Forms
    const [depositoForm, setDepositoForm] = useState({ monto: '', referencia: '' });
    const [extraccionForm, setExtraccionForm] = useState({ monto: '', nroDocumento: '' });
    const [searchFilter, setSearchFilter] = useState('DOCUMENTO');
    const [billetajeApertura, setBilletajeApertura] = useState([]);
    const [totalApertura, setTotalApertura] = useState(0);
    const [billetajeCierre, setBilletajeCierre] = useState([]);
    const [totalCierre, setTotalCierre] = useState(0);

    const formatCurrency = (amount) => new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(amount);

    // --- Initial Load ---
    useEffect(() => {
        const checkSesion = async () => {
            try {
                const resp = await fetch(`${API_BASE_URL}/cajas/sesion-activa`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (resp.status === 204) {
                    setSesionActiva(null);
                } else if (resp.ok) {
                    setSesionActiva(await resp.json());
                }
            } catch (error) {
                console.error("Error checking session:", error);
            } finally {
                setCargandoSesion(false);
            }
        };

        const fetchCatalogos = async () => {
            try {
                const respFP = await fetch(`${API_BASE_URL}/formas-pago`, { headers: { Authorization: `Bearer ${token}` } });
                if (respFP.ok) {
                    let data = await respFP.json();
                    // Priorizar Efectivo (poner al inicio)
                    data = data.sort((a, b) => {
                        if (a.nombre.toUpperCase() === 'EFECTIVO') return -1;
                        if (b.nombre.toUpperCase() === 'EFECTIVO') return 1;
                        return 0;
                    });
                    setFormasPago(data);
                    if (data.length > 0) setFormaPagoId(data[0].id);
                }

                const respCajas = await fetch(`${API_BASE_URL}/mantenimiento/cajas/page?page=0&size=50`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (respCajas.ok) {
                    const data = await respCajas.json();
                    setListaCajas(data.content || []);
                }
            } catch (error) { console.error("Error fetching catalogs:", error); }
        };

        if (token) {
            checkSesion();
            fetchCatalogos();
        }
    }, [token]);

    useEffect(() => {
        setCurrentPage(0);
    }, [activeTab, storeSocio?.id]);

    useEffect(() => {
        if (storeSocio?.id && token) {
            fetchCuentasSocio(storeSocio.id);
            fetchDesembolsosSocio(storeSocio.id);
        }
    }, [storeSocio?.id, token]);

    // --- POS Helpers ---
    const calcularTotal = () => carrito.reduce((sum, i) => sum + (i.montoAPagar || 0), 0);

    const ejecutarCobroMultiple = async () => {
        if (!sesionActiva) return Swal.fire('Error', 'No hay sesión activa', 'error');
        if (carrito.length === 0) return;

        const total = calcularTotal();
        const formaPagoObj = formasPago.find(f => f.id == formaPagoId);
        const esEfectivo = formaPagoObj?.nombre?.toUpperCase() === 'EFECTIVO';

        // 1. Validaciones para Efectivo
        if (esEfectivo) {
            if (!montoRecibido || montoRecibido <= 0) {
                return Swal.fire('Atención', 'Debe ingresar el monto recibido del socio.', 'warning');
            }
            if (montoRecibido < total) {
                return Swal.fire('Monto Insuficiente', `El monto recibido (${formatCurrency(montoRecibido)}) es menor al total (${formatCurrency(total)})`, 'error');
            }
        }

        // 2. Alerta de Confirmación
        const confirm = await Swal.fire({
            title: '¿Confirmar Cobro?',
            html: `¿Está seguro que desea cobrar un total de <br/><b class="text-xl">${formatCurrency(total)} GS</b>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, Finalizar y Cobrar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#ef4444'
        });

        if (!confirm.isConfirmed) return;

        setProcesando(true);
        try {
            const payload = {
                socioId: storeSocio.id,
                formaPagoId: formaPagoId,
                cajaId: sesionActiva.caja.id,
                items: carrito.map(i => ({
                    id: i.id,
                    tipo: i.tipo,
                    monto: i.montoAPagar
                }))
            };

            const resp = await fetch(`${API_BASE_URL}/cajas/cobro-multiple`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (resp.ok) {
                const vuelto = esEfectivo ? montoRecibido - total : 0;

                await Swal.fire({
                    title: 'Cobro Exitoso',
                    html: `
                        <div class="py-4">
                            <p class="text-slate-500 mb-4">La transacción se ha registrado correctamente.</p>
                            ${esEfectivo ? `
                                <div class="bg-emerald-50 border-2 border-emerald-100 p-6 rounded-2xl">
                                    <span class="text-[10px] font-black uppercase text-emerald-600 block mb-1">Entregar Vuelto</span>
                                    <span class="text-3xl font-black text-emerald-700 amount-mono">${formatCurrency(vuelto)} GS</span>
                                </div>
                            ` : ''}
                        </div>
                    `,
                    icon: 'success'
                });

                generarTicketUnificado(carrito, storeSocio, total);
                limpiarCarrito();
                limpiarSocio();
                setMontoRecibido(0);
                setModals({ ...modals, finalizarPago: false });
            } else {
                throw new Error(await resp.text());
            }
        } catch (error) {
            Swal.fire('Error en el Proceso', error.message, 'error');
        } finally {
            setProcesando(false);
        }
    };

    const generarTicketUnificado = (items, cliente, total) => {
        const doc = new jsPDF({ format: [80, 150] });
        doc.setFont("courier", "bold");
        doc.setFontSize(10);
        doc.text('COOPERATIVA PIRIBEBUI', 40, 10, { align: 'center' });
        doc.setFontSize(8);
        doc.text('--------------------------------', 40, 20, { align: 'center' });

        doc.text(`Socio: ${cliente.personaFisica ? `${cliente.personaFisica.nombres} ${cliente.personaFisica.apellidos}` : cliente.personaJuridica.razonSocial}`, 5, 25);
        doc.text(`Doc: ${cliente.personaFisica ? cliente.personaFisica.numeroDocumento : cliente.personaJuridica.ruc}`, 5, 30);
        doc.text(`Fecha: ${new Date().toLocaleString()}`, 5, 35);
        doc.text('--------------------------------', 40, 40, { align: 'center' });

        let y = 45;
        items.forEach(item => {
            doc.text(`${item.concepto.substring(0, 22)}`, 5, y);
            doc.text(`${formatCurrency(item.montoAPagar)}`, 75, y, { align: 'right' });
            y += 5;
        });

        doc.text('--------------------------------', 40, y + 2, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`TOTAL: ${formatCurrency(total)} GS`, 40, y + 8, { align: 'center' });
        doc.save(`Recibo_POS_${new Date().getTime()}.pdf`);
    };

    const fetchCuentasSocio = async (socioId) => {
        try {
            const resp = await fetch(`${API_BASE_URL}/cuentas/socio/${socioId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (resp.ok) setCuentas(await resp.json());
        } catch (e) { console.error("Error fetching accounts", e); }
    };

    const fetchDesembolsosSocio = async (socioId) => {
        setCargandoDesembolsos(true);
        try {
            const resp = await fetch(`${API_BASE_URL}/creditos/socio/${socioId}/resumen`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (resp.ok) {
                const data = await resp.json();
                // Filtramos créditos que están en estado 3 (APROBADO POR COMITE)
                const aprobados = data.filter(d => d.estadoCreditoId === 3);
                setDesembolsos(aprobados);
            }
        } catch (e) { console.error("Error fetching desembolsos", e); }
        finally { setCargandoDesembolsos(false); }
    };

    const handleDesembolso = async (creditoId) => {
        const confirm = await Swal.fire({
            title: '¿Confirmar Desembolso?',
            text: 'Se procederá a acreditar el 100% del monto en la cuenta de ahorros del socio.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, Desembolsar',
            cancelButtonText: 'Cancelar'
        });

        if (!confirm.isConfirmed) return;

        try {
            const params = new URLSearchParams({
                cajaId: sesionActiva.caja.id,
                canalDesembolso: 'CAJA_EFECTIVO'
            });
            if (sesionActiva.caja.sucursal) {
                params.append('sucursalId', sesionActiva.caja.sucursal.id);
            }

            const resp = await fetch(`${API_BASE_URL}/creditos/${creditoId}/desembolsar-existente?${params.toString()}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (resp.ok) {
                Swal.fire('Éxito', 'Crédito desembolsado correctamente y fondos acreditados.', 'success');
                fetchDesembolsosSocio(storeSocio.id);
                fetchCuentasSocio(storeSocio.id); // Actualizar saldos de cuentas
            } else {
                throw new Error(await resp.text());
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleAperturaCaja = async () => {
        if (totalApertura <= 0 || !cajaIdSeleccionada) return Swal.fire('Aviso', 'Datos de apertura incompletos', 'warning');
        try {
            const resp = await fetch(`${API_BASE_URL}/cajas/apertura`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    cajaId: parseInt(cajaIdSeleccionada),
                    montoApertura: totalApertura,
                    detalleBilletaje: billetajeApertura
                })
            });
            if (resp.ok) setSesionActiva(await resp.json());
            else throw new Error(await resp.text());
        } catch (e) { Swal.fire('Error', e.message, 'error'); }
    };

    const handleCierreCaja = async () => {
        try {
            const resp = await fetch(`${API_BASE_URL}/cajas/cierre`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    sesionId: sesionActiva.id,
                    montoCierreDeclarado: totalCierre,
                    detalleBilletaje: billetajeCierre
                })
            });
            if (resp.ok) {
                await Swal.fire('Éxito', 'Caja cerrada correctamente', 'success');
                setSesionActiva(null);
                setModals({ ...modals, cierreCaja: false });
            } else throw new Error(await resp.text());
        } catch (e) { Swal.fire('Error', e.message, 'error'); }
    };

    const handleDeposito = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                cuentaId: operacion.item.id,
                monto: parseFloat(depositoForm.monto),
                referencia: depositoForm.referencia,
                cajaId: sesionActiva.caja.id
            };
            const resp = await fetch(`${API_BASE_URL}/cajas/deposito`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (resp.ok) {
                Swal.fire('Éxito', 'Depósito realizado', 'success');
                setOperacion(null);
                fetchCuentasSocio(storeSocio.id);
            } else throw new Error(await resp.text());
        } catch (e) { Swal.fire('Error', e.message, 'error'); }
    };

    const handleExtraccion = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                cuentaId: operacion.item.id,
                monto: parseFloat(extraccionForm.monto),
                nroDocumento: extraccionForm.nroDocumento,
                cajaId: sesionActiva.caja.id
            };
            const resp = await fetch(`${API_BASE_URL}/cajas/extraccion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (resp.ok) {
                Swal.fire('Éxito', 'Extracción realizada', 'success');
                setOperacion(null);
                fetchCuentasSocio(storeSocio.id);
            } else throw new Error(await resp.text());
        } catch (e) { Swal.fire('Error', e.message, 'error'); }
    };

    // --- EFFECTS ---
    useEffect(() => {
        const main = document.querySelector('main');
        const body = document.querySelector('body');
        const parentWrapper = document.querySelector('.module-wrapper');
        const moduleCard = document.querySelector('.bg-white.rounded-lg.shadow-lg.p-6');

        if (main) {
            main.style.overflow = 'hidden';
            main.style.height = '100vh';
        }
        if (body) body.style.overflow = 'hidden';

        if (parentWrapper) {
            parentWrapper.style.padding = '1rem'; // Re-added padding for air
            parentWrapper.style.overflow = 'hidden';
            parentWrapper.style.height = 'calc(100vh - 80px)';
        }

        if (moduleCard) {
            moduleCard.style.height = '100%'; 
            moduleCard.style.display = 'flex';
            moduleCard.style.flexDirection = 'column';
            moduleCard.style.padding = '0';
            moduleCard.style.overflow = 'hidden';
            moduleCard.style.margin = '0';
            moduleCard.style.borderRadius = '16px';
        }

        return () => {
            if (main) {
                main.style.overflow = 'auto';
                main.style.height = 'auto';
            }
            if (body) body.style.overflow = 'auto';
            if (parentWrapper) {
                parentWrapper.style.padding = '1.5rem';
                parentWrapper.style.height = 'auto';
                parentWrapper.style.overflow = 'visible';
            }
            if (moduleCard) {
                moduleCard.style.height = 'auto';
                moduleCard.style.display = 'block';
                moduleCard.style.padding = '1.5rem';
                moduleCard.style.overflow = 'visible';
            }
        };
    }, []);

    if (cargandoSesion) return <div className="min-h-screen flex items-center justify-center font-bold text-slate-400">Cargando terminal...</div>;

    if (!sesionActiva) {
        return (
            <div className="min-h-screen bg-slate-900 flex flex-col font-sans">
                <header className="bg-slate-800 p-6 flex justify-between items-center border-b border-white/5">
                    <div className="flex items-center gap-4">
                        <div className="bg-blue-600 p-3 rounded-2xl shadow-lg"><Lock className="text-white" size={24} /></div>
                        <div>
                            <h1 className="text-2xl font-black text-white">TERMINAL POS</h1>
                            <p className="text-[11px] text-blue-400 font-bold uppercase tracking-widest">Apertura de Turno</p>
                        </div>
                    </div>
                </header>
                <main className="flex-1 max-w-6xl mx-auto w-full p-8 flex gap-8">
                    <div className="flex-[2] bg-white/5 rounded-3xl p-8 border border-white/10">
                        <h2 className="text-white font-black uppercase text-sm mb-6">Arqueo Inicial</h2>
                        <BilletajeGrid onChange={(items, total) => { setBilletajeApertura(items); setTotalApertura(total); }} />
                    </div>
                    <div className="flex-1 space-y-6">
                        <div className="bg-white rounded-3xl p-8 shadow-xl">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Seleccionar Caja</label>
                            <select
                                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-black mb-6"
                                value={cajaIdSeleccionada}
                                onChange={e => setCajaIdSeleccionada(e.target.value)}
                            >
                                <option value="">---</option>
                                {listaCajas.map(c => <option key={c.id} value={c.id}>{c.nombreCaja.toUpperCase()}</option>)}
                            </select>
                            <button onClick={handleAperturaCaja} className="pos-btn-primary w-full !bg-blue-600">Abrir Turno</button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    const InfoField = ({ label, value, color = "text-slate-900", flexClass = "flex-1 min-w-[100px]" }) => (
        <div className={`${flexClass} animate-premium-in`}>
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-tight mb-1">{label}</label>
            <div className={`bg-slate-50 border border-slate-200/60 rounded-lg px-3 py-2 text-[11px] font-bold ${color} truncate h-9 flex items-center shadow-inner`}>
                {value || <span className="text-slate-300">---</span>}
            </div>
        </div>
    );

    return (
        <div className="caja-pos-container" style={{ flex: 1, height: '100%', overflow: 'hidden' }}>
            <style>{`
                .caja-pos-container { 
                    height: 100vh !important; 
                    display: flex !important; 
                    flex-direction: column !important; 
                    overflow: hidden !important;
                    border-radius: 0 !important;
                    border: none !important;
                }
                .pos-main-grid { 
                    flex: 1 !important; 
                    display: grid !important; 
                    grid-template-columns: 1fr 400px !important; 
                    overflow: hidden !important;
                    min-height: 0 !important;
                }
                .pos-cart { 
                    display: flex !important; 
                    flex-direction: column !important; 
                    height: 100% !important; 
                    overflow: hidden !important;
                }
                .pos-cart-items {
                    flex: 1 !important;
                    overflow-y: auto !important;
                    min-height: 0 !important;
                }
                .pos-cart-footer {
                    flex-shrink: 0 !important;
                    background: white !important;
                    border-top: 1px solid #e2e8f0 !important;
                    box-shadow: 0 -4px 20px rgba(0,0,0,0.05) !important;
                }
            `}</style>
            <header className="pos-header !py-4">
                <div className="flex items-center gap-4 flex-1">
                    {/* Buscador Avanzado */}
                    <div className="global-search-wrapper !max-w-[400px]">
                        <div className="flex items-center bg-slate-100 rounded-xl border-2 border-transparent focus-within:border-blue-500 transition-all overflow-hidden">
                            <select
                                className="bg-transparent border-none text-[9px] font-black uppercase px-3 py-2.5 outline-none text-slate-500 cursor-pointer hover:text-blue-600 transition-colors"
                                value={searchFilter}
                                onChange={e => setSearchFilter(e.target.value)}
                            >
                                <option value="DOCUMENTO">CI / RUC</option>
                                <option value="NOMBRE">NOMBRE</option>
                                <option value="ID">NRO SOCIO</option>
                            </select>
                            <div className="w-[1px] h-5 bg-slate-200"></div>
                            <input
                                type="text"
                                placeholder={`Buscar por ${searchFilter.toLowerCase()}...`}
                                className="bg-transparent flex-1 px-4 py-2.5 text-xs font-bold outline-none text-slate-700"
                                value={storeBusqueda}
                                onChange={e => {
                                    const val = e.target.value;
                                    setBusqueda(val);
                                    buscarSocios(token, val);
                                    if (val.length > 2) setShowDropdown(true);
                                    else setShowDropdown(false);
                                }}
                                onFocus={() => storeBusqueda.length > 2 && setShowDropdown(true)}
                            />
                            <div className="px-4 text-slate-400">
                                {isSearching ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                                ) : <Search size={16} />}
                            </div>
                        </div>

                        {showDropdown && (searchResults.length > 0 || isSearching) && (
                            <div className="search-dropdown animate-premium-in !top-[110%] !rounded-2xl shadow-2xl border-blue-100">
                                {isSearching ? (
                                    <div className="p-8 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">Buscando...</div>
                                ) : (
                                    searchResults.map(s => (
                                        <div key={s.id} className="p-4 hover:bg-blue-50 transition-colors cursor-pointer flex justify-between items-center group" onClick={() => { seleccionarSocio(s, token); setShowDropdown(false); fetchCuentasSocio(s.id); fetchDesembolsosSocio(s.id); }}>
                                            <div>
                                                <span className="font-black text-sm text-slate-800 group-hover:text-blue-700">{s.personaFisica ? `${s.personaFisica.nombres} ${s.personaFisica.apellidos}` : s.personaJuridica.razonSocial}</span>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{s.personaFisica ? s.personaFisica.numeroDocumento : s.personaJuridica.ruc}</p>
                                            </div>
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                <CheckCheck className="text-blue-500" size={16} />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Panel de Datos del Socio (Autocompletado) */}
                    <div className="flex items-center gap-3 flex-1 px-4 border-l border-slate-100">
                        <InfoField
                            label="Socio"
                            value={storeSocio ? (storeSocio.personaFisica ? `${storeSocio.personaFisica.nombres} ${storeSocio.personaFisica.apellidos}` : storeSocio.personaJuridica.razonSocial) : ''}
                            flexClass="flex-[2.5] min-w-[250px]"
                        />
                        <InfoField
                            label="Documento/Ruc"
                            value={storeSocio ? (storeSocio.personaFisica ? storeSocio.personaFisica.numeroDocumento : storeSocio.personaJuridica.ruc) : ''}
                        />
                        <InfoField
                            label="Nro. Socio"
                            value={storeSocio ? storeSocio.id : ''}
                        />
                        <InfoField
                            label="Estado"
                            value={storeSocio ? 'ACTIVO' : ''}
                            color="text-emerald-600"
                        />
                        {storeSocio && (
                            <button onClick={limpiarSocio} className="p-2 hover:bg-red-50 text-slate-300 hover:text-red-500 rounded-lg transition-colors self-end mb-0.5">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-4 ml-auto border-l border-slate-100 pl-4">
                    <div className="text-right">
                        <span className="text-[10px] font-black text-slate-900 uppercase block leading-none">Terminal Autónoma</span>
                        <span className="text-[8px] font-bold text-slate-400 uppercase">{sesionActiva.caja.nombreCaja}</span>
                    </div>
                    <div className="bg-slate-900 p-2 rounded-xl"><Monitor className="text-blue-400" size={18} /></div>
                    <button onClick={() => setModals({ ...modals, cierreCaja: true })} className="p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors">
                        <Lock size={16} />
                    </button>
                </div>
            </header>



            <main className="pos-main-grid">
                <div className="pos-catalog">
                    <div className="folder-tabs">
                        <div className={`folder-tab ${activeTab === 'ASOCIATIVO' ? 'active' : ''}`} onClick={() => setActiveTab('ASOCIATIVO')}>
                            <Shield size={14} /> Aportes / Solidaridad
                        </div>
                        <div className={`folder-tab ${activeTab === 'CREDITOS' ? 'active' : ''}`} onClick={() => setActiveTab('CREDITOS')}>
                            <CreditCard size={14} /> Créditos / Préstamos
                        </div>
                        <div className={`folder-tab ${activeTab === 'SERVICIOS' ? 'active' : ''}`} onClick={() => setActiveTab('SERVICIOS')}>
                            <Settings size={14} /> Servicios / Otros
                        </div>
                        <div className={`folder-tab ${activeTab === 'OPERACIONES' || activeTab === 'AHORROS' ? 'active' : ''}`} onClick={() => setActiveTab('OPERACIONES')}>
                            <Wallet size={14} /> CUENTAS / DEPÓSITOS
                        </div>
                        <div className={`folder-tab ${activeTab === 'DESEMBOLSOS' ? 'active' : ''}`} onClick={() => setActiveTab('DESEMBOLSOS')}>
                            <Banknote size={14} /> DESEMBOLSOS
                        </div>
                    </div>

                    <div className="pos-debt-list">
                        {!storeSocio ? (
                            <div className="empty-state">
                                <div className="empty-icon"><Search size={40} /></div>
                                <p>Busque un socio para comenzar</p>
                            </div>
                        ) : (
                            <div className="excel-grid-container custom-scrollbar">
                                {activeTab === 'DESEMBOLSOS' ? (
                                    <div className="flex flex-col h-full">
                                        <div className="px-4 py-2 bg-emerald-50/50 border-b border-emerald-100 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Banknote size={12} className="text-emerald-500" />
                                                <span className="text-[8px] font-black uppercase text-emerald-600 tracking-widest">
                                                    Créditos Pendientes de Desembolso
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                            {cargandoDesembolsos ? (
                                                <div className="text-center p-8 text-slate-400 font-bold uppercase text-[10px]">Cargando solicitudes...</div>
                                            ) : desembolsos.length === 0 ? (
                                                <div className="text-center p-8 text-slate-400 font-bold uppercase text-[10px]">No hay créditos aprobados pendientes de desembolso</div>
                                            ) : (
                                                desembolsos.map(d => (
                                                    <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row justify-between md:items-center gap-4 shadow-sm hover:border-emerald-200 hover:shadow-emerald-50 transition-all">
                                                        <div>
                                                            <div className="text-[10px] font-black uppercase text-slate-400 mb-1">Crédito Nro. {d.id} • {d.nroOperacion || 'Sin Operación'}</div>
                                                            <div className="text-xl font-black text-slate-800 amount-mono tracking-tighter">{formatCurrency(d.monto)} <span className="text-[10px] text-slate-500">GS</span></div>
                                                            <div className="text-[10px] font-bold text-slate-50 mt-1 uppercase text-slate-500">Plazo: {d.plazoMeses} meses</div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="px-3 py-1 bg-amber-50 text-amber-600 rounded-lg text-[9px] font-black uppercase border border-amber-100">
                                                                {d.estadoNombre}
                                                            </div>
                                                            <button
                                                                onClick={() => handleDesembolso(d.id)}
                                                                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-black text-[10px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-emerald-200 flex items-center gap-2 active:scale-95"
                                                            >
                                                                <Banknote size={14} /> Ejecutar Desembolso
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : (activeTab === 'OPERACIONES' || activeTab === 'AHORROS') ? (
                                    <table className="excel-grid">
                                        <thead>
                                            <tr>
                                                <th>Nro. Cuenta</th>
                                                <th>Tipo Cuenta</th>
                                                <th className="text-right">Saldo Disponible</th>
                                                <th className="text-center">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const ahorroCuentas = cuentas.filter(c => {
                                                    const desc = c.tipoCuenta.descripcion.toUpperCase();
                                                    return !desc.includes('APORTE') && !desc.includes('SOLIDARIDAD');
                                                });

                                                if (ahorroCuentas.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan="4" className="text-center py-10 text-slate-400 font-black uppercase text-[9px]">Sin cuentas de ahorro activas</td>
                                                        </tr>
                                                    );
                                                }

                                                return ahorroCuentas.map(c => (
                                                    <tr key={c.id}>
                                                        <td className="font-black text-slate-900 tracking-tighter">{c.numeroCuenta}</td>
                                                        <td className="font-bold text-slate-500 uppercase text-[9px]">{c.tipoCuenta.descripcion}</td>
                                                        <td className="text-right font-black amount-mono text-emerald-600">{formatCurrency(c.saldoActual)}</td>
                                                        <td className="text-center">
                                                            <div className="flex items-center justify-center gap-1">
                                                                <button
                                                                    onClick={() => setOperacion({ tipo: 'DEPOSITO', item: c })}
                                                                    className="flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg text-[8px] font-black transition-colors uppercase border border-emerald-100"
                                                                >
                                                                    <ArrowDownCircle size={12} /> Depósito
                                                                </button>
                                                                <button
                                                                    onClick={() => setOperacion({ tipo: 'EXTRACCION', item: c })}
                                                                    className="flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 px-2 py-1 rounded-lg text-[8px] font-black transition-colors uppercase border border-red-100"
                                                                >
                                                                    <ArrowUpCircle size={12} /> Extracción
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="flex flex-col h-full">
                                        {/* Indicador de Exigibilidad (Aportes/Solidaridad/Créditos) */}
                                        <div className="px-4 py-2 bg-blue-50/50 border-b border-blue-100 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle size={12} className="text-blue-500 animate-pulse" />
                                                <span className="text-[8px] font-black uppercase text-blue-600 tracking-widest">
                                                    Obligaciones Pendientes ({activeTab})
                                                </span>
                                            </div>
                                            <div className="text-[8px] font-black uppercase text-slate-500">
                                                Sugerido: <span className="text-blue-700 font-bold">
                                                    {formatCurrency([...storeDeudas]
                                                        .filter(d => {
                                                            if (activeTab === 'ASOCIATIVO') return ['APORTE', 'SOLIDARIDAD'].includes(d.tipo);
                                                            if (activeTab === 'CREDITOS') return d.tipo === 'CUOTA';
                                                            if (activeTab === 'SERVICIOS') return ['GASTO_ADM', 'DEUDA', 'OTROS'].includes(d.tipo);
                                                            return false;
                                                        })
                                                        .reduce((sum, d) => sum + d.monto, 0)
                                                    )} GS
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex-1 overflow-y-auto">
                                            <table className="excel-grid">
                                                <thead>
                                                    <tr>
                                                        <th className="w-10 text-center">Sel.</th>
                                                        <th>Vencimiento</th>
                                                        <th>Concepto</th>
                                                        <th className="text-right">Importe</th>
                                                        <th className="text-right">Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const filtered = [...storeDeudas]
                                                            .sort((a, b) => {
                                                                const getNum = (str) => {
                                                                    const m = str.match(/(\d+)/);
                                                                    return m ? parseInt(m[1]) : 0;
                                                                };
                                                                const prA = a.concepto.includes('PRÉSTAMO') ? a.concepto.split('PRÉSTAMO')[1] : '';
                                                                const prB = b.concepto.includes('PRÉSTAMO') ? b.concepto.split('PRÉSTAMO')[1] : '';
                                                                if (prA !== prB) return prA.localeCompare(prB);
                                                                const cuA = getNum(a.concepto);
                                                                const cuB = getNum(b.concepto);
                                                                if (cuA !== cuB) return cuA - cuB;
                                                                return new Date(a.vencimiento) - new Date(b.vencimiento);
                                                            })
                                                            .filter(d => {
                                                                if (activeTab === 'ASOCIATIVO') return ['APORTE', 'SOLIDARIDAD'].includes(d.tipo);
                                                                if (activeTab === 'CREDITOS') return d.tipo === 'CUOTA';
                                                                if (activeTab === 'SERVICIOS') return ['GASTO_ADM', 'DEUDA', 'OTROS'].includes(d.tipo);
                                                                return false;
                                                            });

                                                        const paginated = filtered.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
                                                        const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

                                                        return (
                                                            <>
                                                                {paginated.map(d => {
                                                                    const isOverdue = new Date(d.vencimiento) < new Date().setHours(0, 0, 0, 0);
                                                                    const isSelected = carrito.some(i => i.uniqueId === d.uniqueId);

                                                                    return (
                                                                        <tr
                                                                            key={d.uniqueId}
                                                                            className={`${isSelected ? 'selected' : ''} cursor-pointer transition-colors`}
                                                                            onClick={() => toggleSelection(d)}
                                                                        >
                                                                            <td className="text-center">
                                                                                <div className={`w-5 h-5 mx-auto rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}>
                                                                                    {isSelected && <CheckCheck size={12} className="text-white" />}
                                                                                </div>
                                                                            </td>
                                                                            <td className={`font-bold uppercase text-[9px] ${isOverdue ? 'text-red-500' : 'text-slate-500'}`}>
                                                                                {new Date(d.vencimiento).toLocaleDateString()}
                                                                            </td>
                                                                            <td className="font-black text-slate-700 uppercase">{d.concepto}</td>
                                                                            <td className="amount-cell font-black text-slate-900">
                                                                                <div className="flex flex-col items-end">
                                                                                    <span>{formatCurrency(d.monto)}</span>
                                                                                    {d.montoMinimo > 0 && d.montoMinimo < d.monto && (
                                                                                        <span className="text-[7px] font-black text-blue-500 uppercase tracking-tighter">Mín: {formatCurrency(d.montoMinimo)}</span>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                            <td className="text-right">
                                                                                {isOverdue ? (
                                                                                    <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase bg-red-100 text-red-600 border border-red-200">
                                                                                        Vencido
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase bg-blue-100 text-blue-600 border border-blue-200">
                                                                                        Pendiente
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                {/* Controles de Paginación al final de la tabla */}
                                                                {totalPages > 1 && (
                                                                    <tr className="bg-slate-50/50">
                                                                        <td colSpan="5" className="p-2 border-t border-slate-200">
                                                                            <div className="flex items-center justify-center gap-4 text-[10px] font-black uppercase text-slate-400">
                                                                                <button
                                                                                    disabled={currentPage === 0}
                                                                                    onClick={(e) => { e.stopPropagation(); setCurrentPage(currentPage - 1); }}
                                                                                    className="px-3 py-1 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 disabled:opacity-30 disabled:hover:bg-white"
                                                                                >
                                                                                    ANTERIOR
                                                                                </button>
                                                                                <span>Página {currentPage + 1} de {totalPages}</span>
                                                                                <button
                                                                                    disabled={currentPage >= totalPages - 1}
                                                                                    onClick={(e) => { e.stopPropagation(); setCurrentPage(currentPage + 1); }}
                                                                                    className="px-3 py-1 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 disabled:opacity-30 disabled:hover:bg-white"
                                                                                >
                                                                                    SIGUIENTE
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="pos-cart flex flex-col h-full bg-slate-50 border-l border-slate-200 shadow-[-4px_0_15px_rgba(0,0,0,0.02)]">
                    <div className="p-4 border-b border-slate-200 bg-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-50 rounded-lg">
                                    <Receipt className="text-blue-600" size={16} />
                                </div>
                                <h2 className="font-black uppercase text-[10px] tracking-widest text-slate-900">Resumen de Cobro</h2>
                            </div>
                            <span className="bg-slate-900 text-white px-2 py-0.5 rounded-full text-[10px] font-black">{carrito.length}</span>
                        </div>
                    </div>

                    <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                        {carrito.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center animate-premium-in">
                                <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mb-6 border border-slate-100">
                                    <Receipt className="text-slate-200" size={40} />
                                </div>
                                <h3 className="text-slate-900 font-black uppercase text-[11px] tracking-widest mb-2">Carrito de Operaciones</h3>
                                <p className="text-slate-400 text-[10px] font-bold max-w-[200px] leading-relaxed">
                                    Agregue conceptos desde el catálogo para iniciar el proceso de pago.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="pos-cart-items p-3 space-y-2 custom-scrollbar">
                                    {carrito.map(item => {
                                        const isBelowMin = item.montoMinimo > 0 && item.montoAPagar < item.montoMinimo;
                                        const pendingAmount = item.monto - item.montoAPagar;

                                        return (
                                            <div key={item.uniqueId} className={`bg-white border p-3 rounded-2xl flex flex-col gap-2 transition-all ${isBelowMin ? 'border-red-200 shadow-sm shadow-red-50' : 'border-slate-100 hover:border-blue-100'}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={() => removeFromCart(item.uniqueId)}
                                                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                        <div>
                                                            <span className="block text-[11px] font-black text-slate-800 uppercase leading-tight">{item.concepto}</span>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Total: {formatCurrency(item.monto)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                className={`w-32 text-right font-black text-lg amount-mono p-1 rounded-xl outline-none transition-all ${isBelowMin ? 'text-red-600 bg-red-50' : 'text-slate-900 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-100'}`}
                                                                value={item.montoAPagar}
                                                                onChange={e => updateMontoItem(item.uniqueId, parseInt(e.target.value) || 0)}
                                                            />
                                                            <span className="absolute -right-6 top-1/2 -translate-y-1/2 text-[9px] font-black text-slate-400">GS</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                                                    <div className="flex gap-2">
                                                        {item.montoMinimo > 0 && item.montoMinimo < item.monto && (
                                                            <button
                                                                onClick={() => updateMontoItem(item.uniqueId, item.montoMinimo)}
                                                                className="px-3 py-1.5 border-2 border-blue-100 text-blue-600 rounded-xl text-[9px] font-black uppercase hover:bg-blue-50 hover:border-blue-200 transition-all"
                                                            >
                                                                Mínimo
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => updateMontoItem(item.uniqueId, item.monto)}
                                                            className="px-3 py-1.5 border-2 border-slate-100 text-slate-500 rounded-xl text-[9px] font-black uppercase hover:bg-slate-50 hover:border-slate-200 transition-all"
                                                        >
                                                            Total
                                                        </button>
                                                    </div>

                                                    <div className="text-right">
                                                        {isBelowMin ? (
                                                            <div className="flex items-center gap-1 text-red-500 animate-pulse">
                                                                <AlertTriangle size={10} />
                                                                <span className="text-[9px] font-black uppercase">Mínimo: {formatCurrency(item.montoMinimo)}</span>
                                                            </div>
                                                        ) : pendingAmount > 0 ? (
                                                            <div className="flex flex-col items-end">
                                                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-tighter">Saldo Pendiente</span>
                                                                <span className="text-[10px] font-black text-orange-600 amount-mono">-{formatCurrency(pendingAmount)}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 text-emerald-500">
                                                                <CheckCheck size={12} />
                                                                <span className="text-[9px] font-black uppercase tracking-tighter">Pago Total</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="pos-cart-footer p-4">
                                    <div className="flex justify-between items-end mb-4">
                                        <div>
                                            <span className="text-[9px] font-black uppercase text-slate-400 block tracking-widest mb-1">Total a Cobrar</span>
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-3xl font-black text-blue-600 amount-mono tracking-tighter">{formatCurrency(calcularTotal())}</span>
                                                <span className="text-[10px] font-black text-blue-400">GS</span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-[9px] font-black uppercase text-slate-400 block tracking-widest mb-1">Items</span>
                                            <span className="text-sm font-black text-slate-900">{carrito.length}</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setModals({ ...modals, finalizarPago: true })}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                                    >
                                        <CreditCard size={20} />
                                        PROCESAR PAGO
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* Modal de Finalización de Pago */}
            {modals.finalizarPago && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-premium-in flex flex-col md:flex-row">
                        {/* Sidebar del Modal: Resumen */}
                        <div className="md:w-1/3 bg-slate-50 p-8 border-r border-slate-100">
                            <h3 className="text-slate-400 font-black uppercase text-[10px] tracking-widest mb-6">Detalle del Cobro</h3>
                            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                {carrito.map(item => (
                                    <div key={item.uniqueId} className="flex justify-between gap-2 border-b border-slate-200 pb-2">
                                        <span className="text-[9px] font-bold text-slate-600 uppercase leading-tight">{item.concepto}</span>
                                        <span className="text-[9px] font-black text-slate-900 amount-mono whitespace-nowrap">{formatCurrency(item.montoAPagar)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 pt-6 border-t-2 border-dashed border-slate-200">
                                <span className="text-[10px] font-black text-slate-400 uppercase block mb-1">Monto Total</span>
                                <div className="text-2xl font-black text-slate-900 amount-mono tracking-tighter">
                                    {formatCurrency(calcularTotal())} <span className="text-xs text-slate-400">GS</span>
                                </div>
                            </div>
                        </div>

                        {/* Main Modal: Formas de Pago */}
                        <div className="flex-1 p-8 md:p-10">
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-2xl font-black text-slate-900">Finalizar Transacción</h2>
                                <button
                                    onClick={() => setModals({ ...modals, finalizarPago: false })}
                                    className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="space-y-8">
                                {/* Selección de Forma de Pago */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">Forma de Pago</label>
                                    <div className="relative">
                                        <select
                                            className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black text-slate-700 outline-none focus:border-blue-300 transition-all cursor-pointer appearance-none"
                                            value={formaPagoId}
                                            onChange={e => {
                                                setFormaPagoId(e.target.value);
                                                setMontoRecibido(0);
                                            }}
                                        >
                                            {formasPago.map(f => (
                                                <option key={f.id} value={f.id}>{f.nombre.toUpperCase()}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                            <ArrowDownCircle size={18} />
                                        </div>
                                    </div>
                                </div>

                                {/* Importes de Pago */}
                                {formasPago.find(f => f.id == formaPagoId)?.nombre?.toUpperCase() === 'EFECTIVO' && (
                                    <div className="grid grid-cols-2 gap-6 animate-premium-in">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">Monto Recibido</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-2xl font-black text-blue-600 outline-none focus:border-blue-300 transition-all amount-mono"
                                                    value={montoRecibido || ''}
                                                    onChange={e => setMontoRecibido(parseInt(e.target.value) || 0)}
                                                    placeholder="0"
                                                    autoFocus
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">Vuelto a Entregar</label>
                                            <div className="p-4 bg-emerald-50 border-2 border-emerald-100 rounded-2xl h-[68px] flex items-center justify-center">
                                                <span className={`text-2xl font-black amount-mono tracking-tighter ${montoRecibido >= calcularTotal() ? 'text-emerald-700' : 'text-slate-400'}`}>
                                                    {formatCurrency(Math.max(0, montoRecibido - calcularTotal()))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={ejecutarCobroMultiple}
                                    disabled={
                                        procesando ||
                                        (formasPago.find(f => f.id == formaPagoId)?.nombre?.toUpperCase() === 'EFECTIVO' && montoRecibido < calcularTotal()) ||
                                        carrito.some(item => item.montoMinimo > 0 && item.montoAPagar < item.montoMinimo)
                                    }
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 text-white p-6 rounded-[1.5rem] font-black text-sm uppercase tracking-[0.2em] shadow-xl shadow-emerald-100 flex items-center justify-center gap-4 transition-all relative overflow-hidden group"
                                >
                                    {procesando ? (
                                        <div className="flex items-center gap-3">
                                            <div className="animate-spin rounded-full h-5 w-5 border-3 border-white border-t-transparent"></div>
                                            <span>PROCESANDO...</span>
                                        </div>
                                    ) : (
                                        <>
                                            <CheckCheck size={24} className="group-hover:scale-110 transition-transform" />
                                            <span>CONFIRMAR Y EMITIR COMPROBANTE</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {modals.cierreCaja && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[500] flex items-center justify-center">
                    <div className="bg-white p-10 rounded-[2rem] max-w-lg w-full">
                        <h2 className="text-xl font-black mb-6 uppercase">Arqueo de Cierre</h2>
                        <BilletajeGrid onChange={(items, total) => { setBilletajeCierre(items); setTotalCierre(total); }} />
                        <div className="flex gap-4 mt-10">
                            <button onClick={() => setModals({ ...modals, cierreCaja: false })} className="flex-1 p-4 bg-slate-100 rounded-2xl font-black text-xs uppercase">Cancelar</button>
                            <button onClick={handleCierreCaja} className="flex-1 p-4 bg-red-600 text-white rounded-2xl font-black text-xs uppercase">Cerrar Caja</button>
                        </div>
                    </div>
                </div>
            )}

            {operacion && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[500] flex items-center justify-center p-4">
                    <div className="bg-white p-10 rounded-[2rem] max-w-md w-full">
                        <h2 className="text-xl font-black mb-8 uppercase text-center">{operacion.tipo}</h2>
                        <form onSubmit={operacion.tipo === 'DEPOSITO' ? handleDeposito : handleExtraccion} className="space-y-6">
                            <input type="number" placeholder="Monto" className="w-full p-5 bg-slate-50 rounded-2xl text-2xl font-black text-center" required value={operacion.tipo === 'DEPOSITO' ? depositoForm.monto : extraccionForm.monto} onChange={e => operacion.tipo === 'DEPOSITO' ? setDepositoForm({ ...depositoForm, monto: e.target.value }) : setExtraccionForm({ ...extraccionForm, monto: e.target.value })} />
                            <input type="text" placeholder="Referencia / Nro Boleta" className="w-full p-4 bg-slate-50 rounded-2xl text-sm font-bold text-center" required value={operacion.tipo === 'DEPOSITO' ? depositoForm.referencia : extraccionForm.nroDocumento} onChange={e => operacion.tipo === 'DEPOSITO' ? setDepositoForm({ ...depositoForm, referencia: e.target.value }) : setExtraccionForm({ ...extraccionForm, nroDocumento: e.target.value })} />
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setOperacion(null)} className="flex-1 p-4 bg-slate-100 rounded-2xl font-black text-xs uppercase">Cancelar</button>
                                <button type="submit" className={`flex-1 p-4 rounded-2xl font-black text-xs uppercase text-white ${operacion.tipo === 'DEPOSITO' ? 'bg-emerald-600' : 'bg-red-600'}`}>Confirmar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
